import type { Role, UserStatus } from './roles';

/**
 * A user as the admin screens see them.
 *
 * Note what is absent: there is no password, no credential, nothing
 * authentication-related at all. Identity is the portal's business. This
 * record exists so that *authorisation* — role and status — has somewhere to
 * live that a portal compromise cannot reach.
 */
/**
 * The person as the company's own records describe them.
 *
 * Read live from the application database, never stored here — this system is
 * not a second copy of the HR record, and a cached job title goes stale the
 * day someone is promoted. Null when the employee row cannot be found or the
 * application database is unreachable.
 */
export interface EmployeeRecord {
  /** Payroll code, e.g. INTFE0050. Distinct from the portal's numeric id. */
  employeeCode: string | null;
  /** Name as HR holds it, which may differ from the one the portal asserted. */
  name: string | null;
  officialEmail: string | null;
  /**
   * Their role, as the portal means the word: the tagged `usertype`. This is
   * the string the portal shows the employee for themselves.
   */
  role: string | null;
  /** Further tagged roles, if any. The portal treats the first as primary. */
  otherRoles: string[];
  /** Job title behind the role. Often the same words; occasionally not. */
  designation: string | null;
  /** The team within the department. */
  wing: string | null;
  department: string | null;
  reportingHead: string | null;
  /** Whether they head the department this record resolves to. */
  isHod: boolean;
  /** HR's own wording — "Working", "Resigned", and so on. */
  employmentStatus: string | null;
  /** Date of joining, ISO. */
  joinedOn: string | null;
  /** Still employed according to HR — not the same as console status. */
  employed: boolean;
  exitDate: string | null;
}

export interface UserAdminView {
  id: string;
  employeeId: string;
  email: string;
  name: string;
  department: string | null;
  role: Role;
  status: UserStatus;
  /**
   * Groups the portal asserted at last sign-in. Displayed to admins as a hint
   * when choosing a role — never applied automatically.
   */
  portalGroups: string[];
  firstSeenAt: string;
  lastLoginAt: string | null;
  /** Last time the portal confirmed this employee is still active. */
  lastVerifiedAt: string | null;
  queryCount: number;
  /** Live unmask grant, if any. `until` is null when masking applies normally. */
  unmask: UnmaskGrant;
  /** Where this person's request for access stands. */
  access: AccessRequest;
  /** Whether they may download results, and why. */
  exportPrivilege: ExportPrivilege;
  /**
   * Ids of the connections this person may query.
   *
   * Empty means none — administrators are exempt from the check entirely, so
   * an empty list on an admin is not a restriction. Access is granted per
   * connection because "can use the console" and "can read the finance
   * database" are different decisions.
   */
  dataSourceAccess: string[];
  /**
   * Who they are in the company, read live from the application database.
   * Null when the lookup found nothing or could not run — the screen degrades
   * rather than failing.
   */
  employee: EmployeeRecord | null;
}

/**
 * Where someone's request for console access stands.
 *
 * Derived from status + timestamps rather than stored as its own column, so
 * the request state and the account state cannot drift apart.
 */
export const ACCESS_REQUEST_STATES = [
  'NONE',
  /** Asked, not yet decided. */
  'PENDING',
  'APPROVED',
  /** Asked and refused — never had access. */
  'REJECTED',
  /** Had access, temporarily paused. Can be restored without re-applying. */
  'SUSPENDED',
  /** Had access, withdrawn. */
  'REVOKED',
] as const;
export type AccessRequestState = (typeof ACCESS_REQUEST_STATES)[number];

export interface AccessRequest {
  state: AccessRequestState;
  requestedAt: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  /** The original decision note — why the request was approved or refused. */
  note: string | null;
  /** What the requester says they need the data for, in their own words. */
  purpose: string | null;
  /**
   * Why the account was last suspended or disabled.
   *
   * Deliberately separate from `note`: being suspended six months after
   * approval is a different event with a different reason, and showing the
   * approval note on a suspension screen reads as a contradiction.
   */
  statusReason: string | null;
  statusChangedAt: string | null;
  statusChangedBy: string | null;
}

/** What the console's public access-request screen renders. */
export interface AccessRequestView {
  name: string;
  email: string;
  state: AccessRequestState;
  requestedAt: string | null;
  note: string | null;
}

export interface DecideAccessInput {
  approve: boolean;
  /** Role to grant on approval. Ignored when rejecting. */
  role?: Role;
  /**
   * Connections to grant on approval. Omitted or empty means the person is
   * approved but cannot yet reach any database — never "all of them".
   */
  dataSourceIds?: string[];
  /**
   * Required. On approval it is the audit justification; on rejection it is
   * shown to the person who asked, so write it for them.
   */
  note: string;
}

export interface UpdateUserInput {
  role?: Role;
  status?: UserStatus;
  /** Required. Access changes without a stated reason are unauditable. */
  reason: string;
}

/**
 * How long an unmask grant lasts.
 *
 * Every option expires. There is no "forever" on purpose: a permanent
 * exemption is one nobody remembers granting, and it silently turns every
 * masking rule into decoration. Re-granting takes ten seconds; noticing that
 * someone has held raw access to student records for a year takes an audit.
 */
export const UNMASK_DURATIONS = [
  { hours: 1, label: '1 hour' },
  { hours: 8, label: '1 working day' },
  { hours: 24 * 7, label: '1 week' },
  { hours: 24 * 30, label: '30 days' },
] as const;

/**
 * Per-user override of the download privilege.
 *
 * `override: null` means the role decides. `effective` is what the system
 * actually enforces, so the UI never has to recompute role-plus-override.
 */
export interface ExportPrivilege {
  override: boolean | null;
  /** What the role alone would give. */
  fromRole: boolean;
  effective: boolean;
  reason: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
}

export interface SetExportPrivilegeInput {
  /** null hands control back to the role. */
  override: boolean | null;
  reason: string;
}

export interface UnmaskGrant {
  /** Null when the user has no live grant. */
  until: string | null;
  reason: string | null;
  grantedBy: string | null;
  grantedAt: string | null;
}

export interface GrantUnmaskInput {
  hours: number;
  /** Shown in the audit log. Why this person needs values unmasked. */
  reason: string;
}
